import { inject, Injectable } from '@angular/core';
import { GlobalWebsocketService } from '@approot/global-websocket';
import { Entity } from '@approot/models';
import {
  AlertsService,
  ConfigService,
  TradesCaptureService,
  UserStorageService
} from '@approot/services';
import { clone, sleep } from '@approot/utilities';
import { isEqual } from 'lodash';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { mapHeaders } from './cash-flows-blotter.funcs';
import {
  CashFlowsBlotterState,
  Row,
  RunningState,
  WSRow
} from './cash-flows-blotter.types';

// Note that "entities" and "headers" are not included here as they won't change once set.
const DefaultCashFlowsBlotterState: CashFlowsBlotterState = {
  uuid: null,
  rows: [],
  nItems: null,
  runningState: RunningState.Idle,
  info: null
};

@Injectable({
  providedIn: 'root'
})
export class CashFlowsBlotterService {
  public stateChanged: Observable<CashFlowsBlotterState>;
  private state: CashFlowsBlotterState = {};
  // this servivce is permanent but the component is recreated on each tab navigation
  // so we need to use a ReplaySubject to keep the state
  private state$ = new ReplaySubject<CashFlowsBlotterState>(1);
  private cashFlowsSub: Subscription = null;
  private timer: any = null;
  private websocketService = inject(GlobalWebsocketService);
  private tradesCaptureService = inject(TradesCaptureService);
  private alertsService = inject(AlertsService);
  private configService = inject(ConfigService);
  private userStorageService = inject(UserStorageService);

  constructor() {
    this.setState({
      ...DefaultCashFlowsBlotterState,
      entities: [],
      headers: []
    });
    this.stateChanged = this.state$.asObservable();
    this.loadEntities();
    this.loadCashFlowsBlotterHeaders();
  }

  public update(state: CashFlowsBlotterState): void {
    this.setState(state);
  }

  public async runReport(entity: string) {
    const uuid = await this.websocketService.sendMessage({
      action: 'apiv2_cashFlowsReport',
      payload: { entity }
    });
    console.log('Running Cash Flows Report for', entity, uuid);
    this.setState(
      { ...DefaultCashFlowsBlotterState, uuid, selectedEntity: entity },
      false
    );

    if (this.timer) {
      clearTimeout(this.timer);
    }

    this.timer = setTimeout(() => {
      if (this.state.runningState === RunningState.Running) {
        this.cancelReport('Report timed out');
        console.log('Cash Flows Report timed out');
        this.alertsService.addAlert({
          type: 'error',
          message: 'Cash Flows Report timed out'
        });
        this.ping();
      }
    }, 5 * 60_000);
  }

  public cancelReport(info: string = null) {
    this.unsubscribe();
    this.setState({ ...DefaultCashFlowsBlotterState, info });
    clearTimeout(this.timer);
    this.timer = null;
  }

  /****<PRIVATE>**************************************************************/

  private loadEntities(): void {
    this.tradesCaptureService
      .getEntities()
      .pipe(take(1))
      .subscribe({
        next: (entities: Entity[]) => {
          this.setState({ entities: entities.map(e => e.identifier) });
        }
      });
  }

  private loadCashFlowsBlotterHeaders(): void {
    this.configService
      .getAgGridConfig()
      .pipe(
        take(1),
        map(config => {
          const setting = config.find(
            c => c.identifier === 'defaultCashFlowsBlotterHeaders'
          );
          const headers = config.find(c => c.identifier === 'headers');
          return mapHeaders(setting, headers);
        })
      )
      .subscribe({
        next: headers => this.setState({ headers })
      });
  }

  private setState(state: CashFlowsBlotterState, emit = true) {
    const ouuid = this.state.uuid;
    const newState = { ...this.state, ...state };
    if (isEqual(newState, this.state)) {
      return;
    }

    this.state = clone(newState, true);
    if (emit) {
      this.state$.next(this.state);
    }
    if (state.uuid && state.uuid !== ouuid) {
      this.subscribeToCashFlowsBlotter(state.uuid);
    }
  }

  private unsubscribe() {
    this.cashFlowsSub?.unsubscribe();
    this.cashFlowsSub = null;
  }

  private subscribeToCashFlowsBlotter(uuid: string) {
    this.unsubscribe();
    this.setState({
      uuid,
      rows: [],
      nItems: null,
      runningState: RunningState.Running
    });

    const resubscribe = (iteration: number) => {
      if (iteration > 5) {
        console.error(
          'Failed to subscribe to Cash Flows - max retries exceeded'
        );
        return;
      }
      this.cashFlowsSub?.unsubscribe();
      this.cashFlowsSub = this.websocketService
        .getListener({ uuid, take: Infinity })
        .pipe(
          map((row: WSRow) => {
            return {
              ...row.payload,
              uuid: row.uuid
            } as Row;
          })
        )
        .subscribe({
          next: r => {
            if (r.error) {
              this.handleError(r, true);
            } else if (r.warning) {
              this.handleError(r, false);
            } else if (r.info) {
              this.handleInfo(r);
            } else if (r.item) {
              this.handleData(r);
            }
          },
          complete: async () => {
            console.log('Cash Flows subscription completed');
            await sleep(500);
            resubscribe(iteration + 1);
          },
          error: async err => {
            console.error(err);
            await sleep(500);
            resubscribe(iteration + 1);
          }
        });
    };

    resubscribe(0);
  }

  private handleError(row: Row, abort: boolean) {
    // "No events" is from the "Events Blotter" - backend should be updated
    // to use a different message but for now we'll just check both
    if (
      abort ||
      (row.warning &&
        (row.message === 'No events' || row.message === 'No cash flows'))
    ) {
      this.unsubscribe();
      this.setState(DefaultCashFlowsBlotterState);
    }
    this.alertsService.addAlert({
      type: row.error ? 'error' : 'warning',
      message:
        row.message || 'There was a problem running the cash flows report',
      config: {
        closeButton: true,
        tapToDismiss: true,
        timeOut: 3000
      }
    });
    this.ping();
  }

  private handleInfo(row: Row) {
    this.setState({ info: row.message });
  }

  private handleData(row: Row) {
    const curr = this.state.rows;
    const rows = [...curr, row.item];
    let runningState = this.state.runningState;
    let info = this.state.info;
    if (rows.length === row.nItems) {
      this.unsubscribe();
      runningState = RunningState.Idle;
      info = 'Report loaded';
      this.ping();
    }
    this.setState({ rows, nItems: row.nItems, info, runningState });
  }

  private ping(): void {
    this.userStorageService.ping();
  }
}
